import { useState, useEffect } from 'react';
import { Download, X, Share, ChevronDown, Smartphone, Monitor } from 'lucide-react';

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [platform, setPlatform] = useState('desktop'); // ios, android, desktop

  useEffect(() => {
    if (isStandalone()) return;

    const dismissed = localStorage.getItem('mt_pwa_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed, 10) < 7 * 24 * 60 * 60 * 1000) return;

    if (isIOS()) {
      setPlatform('ios');
      setTimeout(() => setShow(true), 3000);
    } else if (isAndroid()) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform(isAndroid() ? 'android' : 'desktop');
      setTimeout(() => setShow(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    setShowIOSGuide(false);
    localStorage.setItem('mt_pwa_dismissed', Date.now().toString());
  };

  if (!show || isStandalone()) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up" data-testid="pwa-install-prompt">
      <div className="max-w-md mx-auto rounded-lg overflow-hidden"
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 -4px 30px rgba(0,0,0,0.5)', backdropFilter: 'blur(16px)' }}>
        
        {/* Main prompt */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'var(--warning)', color: '#0F1419' }}>
              <span className="font-['Manrope'] font-extrabold text-lg">MT</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                Install Millennials Trucking
              </p>
              <p className="font-['IBM_Plex_Sans'] text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                {platform === 'ios' 
                  ? 'Add to your homescreen for instant access — works offline too.'
                  : 'Get instant access from your homescreen — works offline too.'}
              </p>
            </div>
            <button onClick={handleDismiss} className="p-1 rounded-md transition-colors"
              style={{ color: 'var(--text-faint)' }} data-testid="pwa-dismiss-btn">
              <X size={16} />
            </button>
          </div>

          {/* Platform-specific actions */}
          {platform === 'ios' ? (
            <div className="mt-3 space-y-2">
              <button onClick={() => setShowIOSGuide(!showIOSGuide)}
                className="w-full py-2.5 rounded-md text-xs font-bold font-['Manrope'] uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                style={{ background: 'var(--primary)', color: 'white' }}
                data-testid="pwa-ios-guide-btn">
                <Smartphone size={14} /> How to Install on iPhone
                <ChevronDown size={14} className={`transition-transform ${showIOSGuide ? 'rotate-180' : ''}`} />
              </button>

              {showIOSGuide && (
                <div className="rounded-md p-4 space-y-4" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest" style={{ color: 'var(--text-faint)' }}>
                    // iPhone / iPad Instructions
                  </p>
                  
                  {[
                    { step: '1', icon: '↑', title: 'Tap the Share button', desc: 'Find the share icon at the bottom of Safari (square with arrow pointing up)' },
                    { step: '2', icon: '+', title: 'Tap "Add to Home Screen"', desc: 'Scroll down in the share menu and tap "Add to Home Screen"' },
                    { step: '3', icon: '✓', title: 'Tap "Add"', desc: 'Confirm the name and tap "Add" in the top right corner' },
                  ].map(({ step, icon, title, desc }) => (
                    <div key={step} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 text-sm font-bold font-['JetBrains_Mono']"
                        style={{ background: 'var(--primary-muted)', color: 'var(--primary)', border: '1px solid var(--primary)' }}>
                        {step}
                      </div>
                      <div>
                        <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{title}</p>
                        <p className="font-['IBM_Plex_Sans'] text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{desc}</p>
                      </div>
                    </div>
                  ))}

                  <div className="p-2.5 rounded-md flex items-center gap-2" style={{ background: 'var(--warning-muted)', border: '1px solid var(--warning)' }}>
                    <Share size={14} style={{ color: 'var(--warning)' }} />
                    <p className="text-[11px] font-['IBM_Plex_Sans']" style={{ color: 'var(--warning)' }}>
                      Must use <strong>Safari</strong> browser — Chrome on iPhone doesn't support this.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2 mt-3">
              <button onClick={handleDismiss}
                className="flex-1 py-2.5 rounded-md text-xs font-bold font-['Manrope'] uppercase tracking-wide transition-colors"
                style={{ border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
                Not Now
              </button>
              <button onClick={handleInstall}
                className="flex-1 py-2.5 rounded-md text-xs font-bold font-['Manrope'] uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                style={{ background: 'var(--primary)', color: 'white' }}
                data-testid="pwa-install-btn">
                {platform === 'android' ? <Smartphone size={14} /> : <Monitor size={14} />}
                <Download size={14} /> Install App
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
